import type { FC } from 'react';

import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { PartRegion } from '@/features/storefront/parts/PartRegion';
import type { ProductViewProps } from '@/features/storefront/layouts/product/types';

/**
 * A superstore's product page: the counter, and then the magazine.
 *
 * A furniture superstore sells a sofa twice. The first sale is the forty
 * seconds at the top of this page - price, fabric, size, how many, two ways out
 * - and the second is the week a shopper spends coming back to look at how it
 * is built, what other people said, and what else it could have been. This
 * theme is still drawn around both halves; what changed is who owns them.
 *
 * They used to be two components with eleven hard-coded bands inside them.
 * They are data now: the counter is the components in these two columns, and
 * the magazine is a stack of ordinary sections after this one - both declared
 * in `productPage.ts` as the *defaults* a merchant who has never arranged the
 * page gets, and both editable, reorderable and removable from the customiser
 * the moment they do.
 *
 * So all this file holds is the arrangement, which is the half a theme keeps:
 * a wide left column for the photograph and the facts folded under it, a
 * narrower right one for the transaction, and the gutter between them. The band
 * above the columns and the band below are drawn by the page itself - see
 * `StorefrontProductPage` - so they run at the page's own width in every theme.
 *
 * The buy column does not stick, for the reason this theme's note has always
 * given: the masthead is four decks and sticky, so a pinned column would have
 * to start below 280px of it, and what is left is not enough to hold a price,
 * three pickers and two buttons.
 */
export const Product: FC<ProductViewProps> = () => (
  <StorefrontContainer className="grid gap-10 py-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:gap-16">
    <PartRegion name="gallery" className="min-w-0" />
    <PartRegion name="main" className="min-w-0" />
  </StorefrontContainer>
);
